import React from 'react';
import Tooltip from '../common/Tooltip';

interface FeedbackLoopResultsDisplayProps {
  data: {
    action: string;
    feedback: string;
    max_attempts: number;
    next_step_id: string;
    iteration_number: number;
    loop_iteration_id: number;
  };
  workflowSteps?: Array<{ orchestration_step_id: string | number; step_type: string; description?: string }>;
}

const FeedbackLoopResultsDisplay: React.FC<FeedbackLoopResultsDisplayProps> = ({ data, workflowSteps = [] }) => {
  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const processContent = (content: string): string => {
    // Convert URLs to clickable links
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const withLinks = content.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:text-primary-800 underline">${url}</a>`;
    });
    
    // Parse markdown if available
    try {
      const { marked } = require('marked');
      return marked.parse(withLinks) as string;
    } catch (error) {
      return withLinks;
    }
  };

  const getStatusColor = (iterationNumber: number, maxAttempts: number) => {
    const progress = iterationNumber / maxAttempts;
    if (progress >= 0.8) return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
    if (progress >= 0.6) return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400';
    if (progress >= 0.4) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
  };

  const getStatusText = (iterationNumber: number, maxAttempts: number) => {
    const progress = iterationNumber / maxAttempts;
    if (progress >= 0.8) return 'High Attempts';
    if (progress >= 0.6) return 'Moderate Attempts';
    if (progress >= 0.4) return 'Low Attempts';
    return 'Early Stage';
  };

  // Get next step information
  const getNextStepInfo = () => {
    if (import.meta.env.DEV) {
      console.log('=== Step Matching Debug ===');
      console.log('Looking for next_step_id:', data.next_step_id, 'Type:', typeof data.next_step_id);
      console.log('Available workflow steps:', workflowSteps);
      workflowSteps.forEach((step, index) => {
        console.log(`Step ${index}:`, {
          id: step.orchestration_step_id,
          idType: typeof step.orchestration_step_id,
          matches: step.orchestration_step_id === data.next_step_id,
          strictEqual: step.orchestration_step_id === data.next_step_id,
          looseEqual: step.orchestration_step_id == data.next_step_id
        });
      });
    }
    
    // Try exact match first
    let nextStep = workflowSteps.find(s => s.orchestration_step_id === data.next_step_id);
    
    // If not found, try string comparison (in case one is string and one is number)
    if (!nextStep) {
      nextStep = workflowSteps.find(s => String(s.orchestration_step_id) === String(data.next_step_id));
    }
    
    if (import.meta.env.DEV) {
      console.log('Found nextStep:', nextStep);
    }
    
    if (nextStep) {
      return {
        stepType: nextStep.step_type,
        description: nextStep.description || 'No description available'
      };
    }
    return null;
  };

  const nextStepInfo = getNextStepInfo();

  return (
    <div className="space-y-4">
      <div className="relative border rounded-lg shadow-sm p-4 transition bg-white dark:bg-cap-dark">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="font-bold text-purple-700 dark:text-purple-300">Feedback Loop</span>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(data.iteration_number, data.max_attempts)}`}>
              {getStatusText(data.iteration_number, data.max_attempts)}
            </span>
          </div>
          <Tooltip content="Copy feedback loop data">
          <button
            className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            onClick={() => handleCopy(JSON.stringify(data, null, 2))}
          >
            Copy
          </button>
          </Tooltip>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span>Iteration {data.iteration_number} of {data.max_attempts}</span>
            <span>{Math.round((data.iteration_number / data.max_attempts) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                data.iteration_number / data.max_attempts >= 0.8 ? 'bg-red-500' :
                data.iteration_number / data.max_attempts >= 0.6 ? 'bg-orange-500' :
                data.iteration_number / data.max_attempts >= 0.4 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min((data.iteration_number / data.max_attempts) * 100, 100)}%` }}
            ></div>
          </div>
        </div>

        {/* Feedback Content */}
        <div className="space-y-3">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-700 dark:text-gray-300">Feedback</span>
              <Tooltip content="Copy feedback">
              <button
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                onClick={() => handleCopy(data.feedback)}
              >
                Copy
              </button>
              </Tooltip>
            </div>
            <div
              className="prose prose-sm dark:prose-invert max-w-none border border-gray-100 dark:border-gray-700 rounded p-2 bg-white dark:bg-cap-dark"
              style={{ minHeight: '3rem' }}
              dangerouslySetInnerHTML={{ __html: processContent(data.feedback) }}
            />
          </div>

          {/* Loop Details */}
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="border border-gray-200 dark:border-gray-700 rounded p-2 bg-white dark:bg-cap-dark">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Next Step</div>
              <div className="text-xs text-gray-700 dark:text-gray-300">
                {nextStepInfo ? (
                  <div>
                    <div className="font-medium">{nextStepInfo.stepType}</div>
                    <div className="text-gray-600 dark:text-gray-400">{nextStepInfo.description}</div>
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400 italic">
                    Step information not available
                    {import.meta.env.DEV && (
                      <div className="text-xs mt-1">
                        <div>Looking for step ID: {data.next_step_id}</div>
                        <div>Available steps: {workflowSteps.length}</div>
                        {workflowSteps.length > 0 && (
                          <div>Step IDs: {workflowSteps.map(s => s.orchestration_step_id).join(', ')}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackLoopResultsDisplay; 